import type { Components } from 'react-markdown';
import type { z } from 'zod';
import React from 'react';
import { Building, ExternalLink, Globe } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import type { EcosystemRecordSchema, LegacyEcosystemRecordSchema } from '@ballerine/common';
import { checkIsDomainOrUrl } from '@ballerine/common';

import { useForceExpandAccordions } from '@/common/providers/PrintContext';
import { TextWithNAFallback } from '@/components/atoms/TextWithNAFallback';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/molecules/Accordion';

type EcosystemCardListProps = {
  ecosystem?: Array<z.infer<typeof EcosystemRecordSchema>> | null;
};

export const EcosystemCardList: React.FC<EcosystemCardListProps> = ({ ecosystem }) => {
  const forceExpandAccordions = useForceExpandAccordions();

  if (!ecosystem || ecosystem.length === 0) {
    return (
      <div className="bg-card text-card-foreground rounded-lg border px-6 py-4 shadow-sm">
        <TextWithNAFallback>No ecosystem detected for the website.</TextWithNAFallback>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {ecosystem.map((item, index) => {
        const relationship = getEcosystemRelationship(item);
        const details = getEcosystemDetails(item);
        const accordionValue = `item-${index}`;

        return (
          <div
            key={accordionValue}
            className="rounded-lg border border-slate-200 bg-white shadow-sm"
          >
            <Accordion
              type="single"
              collapsible
              className="w-full"
              defaultValue={forceExpandAccordions ? accordionValue : undefined}
            >
              <AccordionItem value={accordionValue} className="border-none">
                <AccordionTrigger className="rounded-lg px-4 py-2 hover:bg-slate-50 hover:no-underline data-[state=open]:rounded-b-none">
                  <div className="flex w-full items-center gap-4 text-left">
                    <div className="flex flex-shrink-0 items-center justify-center">
                      {getEntityIcon(item.domain)}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <p
                        className="notranslate max-w-md break-words text-sm font-medium"
                        translate="no"
                      >
                        {renderDomainOrEntity(item.domain)}
                      </p>
                      {relationship && (
                        <p className="max-w-xl truncate text-xs text-slate-400">
                          Relationship - {relationship}
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="grid gap-3 border-t border-slate-100 pb-4 pl-10 pr-6 pt-4">
                  <EcosystemDetails details={details} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        );
      })}
    </div>
  );
};

type EcosystemDetail = {
  label: string;
  value: string;
};

const addProtocolIfMissing = (url: string) => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `http://${url}`;
};

const getEntityIcon = (domain: string) => {
  if (checkIsDomainOrUrl(domain)) {
    return <Globe className="h-5 w-5 text-slate-400" />;
  }

  return <Building className="h-5 w-5 text-slate-400" />;
};

const renderDomainOrEntity = (domainOrEntity: string) => {
  if (checkIsDomainOrUrl(domainOrEntity)) {
    const domainWithProtocol = addProtocolIfMissing(domainOrEntity);

    return (
      <a
        href={domainWithProtocol}
        onClick={e => e.stopPropagation()}
        target="_blank"
        rel="noopener noreferrer"
        title={domainWithProtocol}
        className="hover:underline"
      >
        {domainOrEntity}
        <ExternalLink className="mb-[2px] ml-1 inline-block h-3 w-3 text-blue-600 hover:text-blue-800" />
      </a>
    );
  }

  return (
    <span className="break-words text-slate-900" title={domainOrEntity}>
      {domainOrEntity}
    </span>
  );
};

const isLegacyEcosystemItem = (
  ecosystemItem: z.infer<typeof EcosystemRecordSchema>,
): ecosystemItem is z.infer<typeof LegacyEcosystemRecordSchema> => {
  return 'relatedNode' in ecosystemItem;
};

const getEcosystemRelationship = (item: z.infer<typeof EcosystemRecordSchema>) => {
  if (isLegacyEcosystemItem(item)) {
    return item.relatedNodeType;
  }

  return item.relationship;
};

const getEcosystemDetails = (item: z.infer<typeof EcosystemRecordSchema>): EcosystemDetail[] => {
  if (isLegacyEcosystemItem(item)) {
    return [{ label: item.relatedNodeType, value: item.relatedNode }];
  }

  return item.nodes.map(({ type, value }) => ({
    label: type,
    value,
  }));
};

const formatMarkdownContent = (value: string | null | undefined) =>
  (value ?? '').replace(/\n\s*-\s*/g, ' ');

const EcosystemDetails: React.FC<{ details: EcosystemDetail[] }> = ({ details }) => {
  if (details.length === 0) {
    return null;
  }

  return (
    <>
      {details.map(({ label, value }, index) => (
        <div
          key={`${index}-${label}`}
          className="grid grid-cols-[120px,1fr,auto] items-start gap-4 border-b border-slate-50 py-2 text-sm last:border-0"
        >
          <TextWithNAFallback className="break-words text-slate-400">{label}</TextWithNAFallback>
          <div className="break-words leading-relaxed">
            <ReactMarkdown components={markdownComponents}>
              {formatMarkdownContent(value)}
            </ReactMarkdown>
          </div>
        </div>
      ))}
    </>
  );
};

const markdownComponents: Components = {
  a: ({ node, ...props }) => (
    <a
      className="mx-1 inline-block rounded-full bg-indigo-50 px-2 py-1 text-xs text-neutral-400 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  em: ({ node, ...props }) => <em {...props} />,
  li: ({ node, ...props }) => <li className="max-w-[60ch]" {...props} />,
};
